import { EnumDataType, EnumDataTypeMD } from '../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/EnumDataType';
import { EnumErrorLevel } from '../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/EnumErrorLevel';
import { ValidationResult } from '../../../../../../api/com/MetaPattern/Core/Infrastructure/Common/ValidationResult';
import { Double2D } from '../../../../../../api/com/MetaPattern/MDA/Designer/Base/Double2D';
import { EnumDisplayLanguage } from '../../../../../../api/com/MetaPattern/MDA/Designer/Base/EnumDisplayLanguage';
import { NodeShape } from '../../../../../../api/com/MetaPattern/MDA/Designer/Shape/NodeShape';
import { BaseElement } from '../../../../../../api/com/MetaPattern/MDA/MD/Base/BaseElement';
import * as joint from 'jointjs';
import { ValidateHelper } from '../../../Product/IDE/Util/ValidateHelper';
import { MDExtendService } from '../../MD/MDExtendService';
import { ValidationResultHelper } from '../Base/ValidationResultHelper';
import { Diagram } from '../Repository/Diagram';
import { MDShape } from './MDShape';
import { ShapeModel } from './ShapeModel';
import { MDAConstant } from '../Base/MDAConstant';

/**
 * 元数据图形的基类
 */
export abstract class MDBaseShape extends joint.shapes.basic.Generic implements MDShape {
  private _model: ShapeModel; // 元数据
  private _shape: NodeShape; // 图形元数据
  private _isSelected: boolean; // 是否选中
  private _displayLanguage: EnumDisplayLanguage; // 显示语言
  private _isLock: boolean; // 是否锁定

  /**
   * 构建图形：设置元数据、图形元数据
   * @param elementShape 图形元数据
   * @param model 元数据
   * @param isFromJSON 是否从JSON恢复
   */
  protected setBaseMD (elementShape: NodeShape, model: ShapeModel, isFromJSON: boolean) {
    this._model = model;
    this.set('model', this._model);
    this._shape = elementShape;
    this.set('shape', this._shape);
    if (!isFromJSON) {
      this.fromMD();
    }
    this.updateRectangles();
  }

  /** 设置元数据模型 */
  protected setShapeModel(model: ShapeModel): void {
    this._model = model;
    this.set('model', this._model);
  }

  /** 删除行 */
  protected deleteRow(designId: string): BaseElement | null {
    for (let i = 0; i < this._model.compartments.length; i++) {
      const index = this._model.compartments[i].findIndex(item => item.designId === designId);
      if (index > -1) {
        const row = this._model.compartments[i].splice(index, 1)[0];
        MDExtendService.setElementListSequence(this._model.compartments[i], true);
        return row;
      }
    }
    return null;
  }

  protected getDisplayTitle(element: BaseElement) {
    if (this._displayLanguage === EnumDisplayLanguage.English) {
      return element.name;
    }
    return element.displayName;
  }

  protected getDataType(dataTypeEnum: EnumDataType, referenceType: string) {
    if (dataTypeEnum === EnumDataType.Reference || dataTypeEnum === EnumDataType.UniqueKey) {
      return referenceType;
    }
    return EnumDataTypeMD.getItemByValue(dataTypeEnum).name;
  }

  /** 获取模型封装的元数据 */
  element(): BaseElement {
    return this._model.element;
  }

  /** 图形包含的子元素的集合 */
  childElements(): Array<BaseElement> {
    const elements = new Array<BaseElement>();
    this._model.compartments.forEach(item => elements.push(...item));
    return elements;
  }

  /** 获取图形对应的数据模型 */
  model(): ShapeModel {
    return this._model;
  }

  /** 获取图形元数据 */
  shape(): NodeShape {
    return this._shape;
  }

  get displayLanguage() {
    return this._displayLanguage;
  }

  /** 是否收缩，false代表展开 */
  get isCollapse(): boolean {
    if (this._model !== null) {
      return this._model.isCollapse;
    }
    return false;
  }

  /** 收缩或展开 */
  collapse() {
    this._model.isCollapse = !this._model.isCollapse;
    this._shape.isExpend = !this._shape.isExpend;
    this.refresh();
  }

  /** 当前图形是否被选中 */
  isSelect(): boolean {
    return this._isSelected;
  }

  /** 选中图形 */
  select(): string {
    this.attr({ body: { stroke: '#ffffff' } });
    this._isSelected = true;
    return this._model.element.designId;
  }

  /** 去掉图形的选中状态 */
  unSelect() {
    this.attr({ body: { stroke: '#545c64' } });
    this._isSelected = false;
    return this._model.element.designId;
  }

  /** 是否锁定，锁定的元素，不能进行修改操作 */
  isLock(): boolean {
    return this._isLock;
  }

  /** 锁定图形 */
  lock(): void {
    this._isLock = true;
  }

  /** 解锁图形 */
  unLock(): void {
    this._isLock = false;
  }

  /** 选中某一行 */
  selectRow(row: string) {
    if (row === null) {
      this.set('curRowDesignId', '');
    } else {
      this.set('curRowDesignId', row);
    }
  }

  /** 当前图形中选中的行对应的元数据 */
  selectedElement(): BaseElement {
    const designId = this.get('curRowDesignId');
    if (designId !== '') {
      return this.getRowElement(designId);
    }
    return null;
  }

  /** 获取designId对应的行的元数据 */
  getRowElement(designId: string): BaseElement | null {
    for (let i = 0; i < this._model.compartments.length; i++) {
      const result = this._model.compartments[i].find(element => element.designId === designId);
      if (result) {
        return result;
      }
    }
    return null;
  }

  /**
   * 设置显示语言
   * @param displayLanguage 显示语言
   */
  setDisplayLanguage (displayLanguage: EnumDisplayLanguage) {
    this._displayLanguage = displayLanguage;
    this.updateRectangles();
  }

  /**
   * 适应内容，即自定义尺寸
   * TODO：由于原有的resize方法不起效
   * @param setWidth 设置的宽度
   * @param setHeight 设置的高度
   */
  fitToSize(setWidth: number, setHeight: number) {
    this.resize(setWidth, setHeight);
    this.toMD();
    this.updateRectangles(setWidth, setHeight);
  }

  /** 刷新图形 */
  refresh() {
    this.updateRectangles();
  }

  /** 将JOINT的数据写入图形元数据 */
  toMD(): void {
    if (this._shape) {
      let isModified = false;
      if (!(this._shape.startPoint.x === this.position().x && this._shape.startPoint.y === this.position().y)) {
        this._shape.startPoint = new Double2D(this.position().x, this.position().y);
        isModified = true;
      }
      if (!(this._shape.curSize.x === this.size().width && this._shape.curSize.y === this.size().height)) {
        this._shape.curSize = new Double2D(this.size().width, this.size().height);
        isModified = true;
      }
      if (this._shape.isExpend !== !this.get('model').isCollapse) {
        this._shape.isExpend = !this.get('model').isCollapse;
        isModified = true;
      }
      if (isModified) {
        this._shape.modify();
      }
    }
  }

  /** 将图形元数据写入JOINT */
  fromMD(): void {
    if (this._shape) {
      this.position(this._shape.startPoint.x, this._shape.startPoint.y);
      // if (this._shape.curSize.x > 0 && this._shape.curSize.y > 0) {
      this.size(this._shape.curSize.x, this._shape.curSize.y);
      // }
      this.get('model').isCollapse = !this._shape.isExpend;
    }
  }

  /** 校验模型 */
  validate(): Array<ValidationResult> {
    const errors = new Array<ValidationResult>();
    const ele = this.element();
    if (ele) {
      if (!ValidateHelper.IsNotNullOrEmpty(ele.name)) {
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, ele.designId, ele.designId, '必须填写名称'));
      } else {
        // 校验名称不能为一个 关键字数组里面成员的校验
        if (MDAConstant.KeyWords.Java.includes(ele.name) || MDAConstant.KeyWords.TypeScript.includes(ele.name) || MDAConstant.KeyWords.JavaScript.includes(ele.name) || MDAConstant.KeyWords.Sql.includes(ele.name) || MDAConstant.KeyWords.YuanXingCloud.includes(ele.name)) {
          errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, ele.designId, ele.designId, '名称不能为关键字'));
        }
      }
      if (!ValidateHelper.IsNotNullOrEmpty(ele.displayName)) {
        errors.push(ValidationResultHelper.createResult(EnumErrorLevel.Error, ele.designId, ele.designId, '必须填写显示名称'));
      }
    }
    return errors;
  }

  /** 执行菜单命令 */
  doAction(action: string, designId?: string): BaseElement | null {
    return null;
  }

  /** 图形的前端类型 */
  abstract shapeType(): string;

  /** 根据链接的类型，设置鼠标的样式 */
  abstract setLinkType(linkType: string): void;

  /**
   * 克隆图形，包括图形内部所有元素，不包括与其他图形的关系
   * @param diagram 克隆后所属的图表
   * @param elements 克隆的所有元数据都插入数组返回
   */
  abstract cloneShape(diagram: Diagram, elements: Array<BaseElement>): MDShape;

  /** 添加子元素的克隆 */
  abstract addChildClone(element: BaseElement, elements: Array<BaseElement>): BaseElement | null;

  protected abstract updateRectangles(setWidth?: number, setHeight?: number): void;
}

export const BaseShapeView = joint.dia.ElementView.extend({
  initialize() {
    joint.dia.ElementView.prototype.initialize.apply(this, []);

    this.listenTo(this.model, 'uml-update', () => {
      // console.log('TypeOnePartShapeView uml-update');
      this.update();
      this.resize();
    });
  }
});
